import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { AppShell, type Level } from '../components/AppShell';
import { Loader } from '../components/Loader';
import { Wordmark } from '../components/Wordmark';
import { DecisionView } from '../dashboard/DecisionView';
import { InsightView } from '../dashboard/InsightView';
import { IntelligenceView } from '../dashboard/IntelligenceView';
import { useBrand, useBrands, useSector } from '../dashboard/api';
import { useErrorMessage } from '../lib/useErrorMessage';

/**
 * Dashboard: guscio unico per lo stato "nessun brand" e per le tre viste.
 *
 * Il brand vive nell'URL (`/brand/:slug`) invece che nello stato locale, così
 * una vista è condivisibile e il tasto Indietro fa quello che l'utente si
 * aspetta. Il guscio non si rimonta al cambio brand.
 */
export function DashboardPage(): ReactNode {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const describeError = useErrorMessage();

  const [level, setLevel] = useState<Level>(1);
  const brands = useBrands();
  const brand = useBrand(slug);
  // Il riepilogo di settore serve solo al Level 3: si carica quando serve.
  const sector = useSector(level === 3 ? brand.data?.brand.sectorKey : undefined);

  const currentSlug = slug ?? null;
  const listed = brands.data?.find((b) => b.slug === currentSlug);

  function onSelectBrand(next: string): void {
    setLevel(firstEnabledLevel());
    void navigate(`/brand/${next}`);
  }

  function firstEnabledLevel(): Level {
    if (user?.features.insight) return 1;
    if (user?.features.decision) return 2;
    return 3;
  }

  return (
    <AppShell
      brands={brands.data ?? []}
      brandsLoading={brands.isPending}
      currentSlug={currentSlug}
      onSelectBrand={onSelectBrand}
      sectorLabel={listed?.sector ?? null}
      level={level}
      onLevelChange={setLevel}
    >
      {currentSlug === null ? (
        <WelcomePanel />
      ) : brand.isPending ? (
        <Loader />
      ) : brand.isError ? (
        <LevelMessage title={describeError(brand.error) ?? t('errors.internal_error')} />
      ) : brand.data ? (
        <div className="level-panel active">
          {level === 1 ? <InsightView brand={brand.data.brand} /> : null}
          {level === 2 ? <DecisionView brand={brand.data.brand} /> : null}
          {level === 3 ? (
            sector.isPending ? (
              <Loader />
            ) : sector.isError ? (
              <LevelMessage title={describeError(sector.error) ?? t('errors.internal_error')} />
            ) : sector.data ? (
              <IntelligenceView brand={brand.data.brand} sector={sector.data} />
            ) : null
          ) : null}
        </div>
      ) : null}
    </AppShell>
  );
}

/** Schermata di benvenuto dell'Allegato A, mostrata finché non si sceglie un brand. */
function WelcomePanel(): ReactNode {
  const { t } = useTranslation();

  return (
    <div className="welcome-panel active">
      <div className="saturn-body" />
      <div className="welcome-inner">
        <div className="welcome-wordmark">
          <Wordmark height={60} center />
        </div>
        <div className="welcome-subtitle">{t('auth.platform')}</div>
        <div className="welcome-divider" />
        <div className="welcome-prompt">{t('home.prompt')}</div>
      </div>
    </div>
  );
}

/** Riquadro neutro del riferimento, usato per i livelli non ancora convertiti. */
function LevelMessage({ title }: { title: string }): ReactNode {
  return (
    <div className="level-placeholder">
      <div className="level-placeholder-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M3 9h18M9 21V9" />
        </svg>
      </div>
      <h3>{title}</h3>
    </div>
  );
}
